import React, { useEffect, useRef, useState, useCallback } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import BaseButton from "../../components/Common/Buttons/BaseButton";
import Radio from "../../components/Common/Form/Radio"
import TextField from "../../components/Common/Form/TextField";
import SelectBox from "../../components/Common/Form/SelectBox";
import Counter from "../../components/EcWebsettlement/Counter";
import ConfirmationModal from "../../components/Common/ConfirmationModal";
import { useRadio } from "../../hooks/Common/useRadio"
import SecondaryButton from "../../components/Common/Buttons/SecondaryButton";
import { EcReturn, EcReturnSku, ProjectInformation, PurchaseEcReturnFormValues, DiscountSetting } from "../../types/pages/EcWebsettlement/purchase_ec_return";
import { QUESTION_TYPE } from "../../utils/constants"
import { Control, FieldErrors, UseFormGetValues, UseFormSetValue, UseFormWatch } from "react-hook-form";
import LinkifyText from "../../components/Common/LinkifyText";
import CouponArea from "../EcReturn/Websettlement/CouponArea";
import { HttpRequest } from "../../utils/common";
import CouponBannerIntroduction500 from "../../../images/紹介割引_500円_1280_480.png";
import CouponBannerIntroduction1000 from "../../../images/紹介割引_1000円_1280_480.png";
import CouponBannerRepeat500 from "../../../images/リピート割引_500円_1280_480.png";
import CouponBannerRepeat1000 from "../../../images/リピート割引_1000円_1280_480.png";
import CouponBannerCross500 from "../../../images/応援ハシゴ割引_500円_1280_480.png";
import CouponBannerCross1000 from "../../../images/応援ハシゴ割引_1000円_1280_480.png";
import AuctionRemainingTime from "../../components/Common/AuctionRemainingTime";
import AuctionBidHistoriesList from "../../components/Common/AuctionBidHistoriesList";

const AUCTION_MIN_BID_INCREMENT = 500;

type Props = {
  ecReturn: EcReturn,
  projectInformation: ProjectInformation,
  control: Control<PurchaseEcReturnFormValues, any>,
  setValue: any,
  getValues: UseFormGetValues<PurchaseEcReturnFormValues>,
  watch: UseFormWatch<PurchaseEcReturnFormValues>,
  errors: FieldErrors<PurchaseEcReturnFormValues>,
  totalAmount: () => number,
  priceByAmount: () => number,
  onCancel: () => void,
  onQuickBid?: () => void,
  monthly_destination_value: number | null,
  commission: number,
  is_draft?: boolean,
  discount_setting?: DiscountSetting,
  is_target_for_discount: boolean,
}

const SelectReturn = ({
  ecReturn,
  projectInformation,
  control,
  setValue,
  getValues,
  watch,
  errors,
  totalAmount,
  priceByAmount,
  onCancel,
  onQuickBid,
  monthly_destination_value,
  commission,
  is_draft,
  discount_setting,
  is_target_for_discount,
}: Props) => {
  const initialMinAmount = ecReturn.is_auction
    ? (ecReturn.auction_minimum_next_bid ?? ecReturn.price)
    : 500;
  // オークション時は入札額(additional_amount)で判定する。totalAmount() は commission を含むため過大評価になる
  // フォームの初期値が空/不正値のケースに備えて Number(... || 0) で正規化
  const initialBidValue = Number(getValues("additional_amount") || 0);
  const initialAuctionBidValid = ecReturn.is_auction && initialBidValue >= initialMinAmount;
  const [isTotalAmountValid, setIsTotalAmountValid] = useState<boolean>(
    ecReturn.is_auction ? initialAuctionBidValid : totalAmount() >= initialMinAmount
  );
  const [couponError, setCouponError] = useState<string>("");

  const discountAmount = discount_setting?.discount_amount
  const discountBannersMap: Record<string, Record<number, string>> = {
    repeat: {
      500: CouponBannerRepeat500,
      1000: CouponBannerRepeat1000,
    },
    cross: {
      500: CouponBannerCross500,
      1000: CouponBannerCross1000,
    },
    introduction: {
      500: CouponBannerIntroduction500,
      1000: CouponBannerIntroduction1000,
    },
  };

  const discountBanner = discountBannersMap[discount_setting?.discount_type]?.[discount_setting?.discount_amount];

  const appliedCoupon = watch("appliedCoupon");

  const handleApplyCoupon = (code: string) => {
    setCouponError("");
    
    HttpRequest.post(
      `/api/investments/${projectInformation.investment_id}/validate_coupon`,
      { coupon_code: code }
    )
      .then(response => {
        const discount = response.data.discount_amount;
        if (discount > totalAmount()) {
          setCouponError("クーポンの割引額が合計金額を超えています");
          setValue("appliedCoupon", null);
          return;
        }

        setValue("appliedCoupon", { code, amount: response.data.discount_amount });
      })
      .catch(error => {
        if (error.response?.status === 422) {
          const msg = error.response.data?.message ?? 
                     error.response.data?.error ?? 
                     "クーポンの適用に失敗しました";
          setCouponError(msg);
        } else {
          setCouponError("通信エラーが発生しました");
        }
        setValue("appliedCoupon", null);
      });
  };

  const handleRemoveCoupon = () => {
    setValue("appliedCoupon", null);
    setCouponError("");
  };
  
  const updateTotalAmountValidity = useCallback(() => {
    setIsTotalAmountValid(totalAmount() >= 500);
  }, [totalAmount]);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [showMoreBtn, setShowMoreBtn] = useState<boolean>(false);
  const {
    isActive: isQuestionTypeSelectActive,
    onChange: onQuestionTypeSelectChange
  } = useRadio(false);
  const { isActive: isSkuActive, onChange: onSkuChange } = useRadio(false);
  const ecReturnContentRef = useRef(null);
  const [nowAnswers, setNowAnswers] = useState(getValues("answers"));

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const ecReturnSlider = {
    infinite: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    dots: true,
    responsive: [
      {
        breakpoint: 1280,
        settings: {
          arrows: true,
        },
      },
    ],
  };

  const additionalAmountOptions = [
    "0",
    "500",
    "1,000",
    "1,500",
    "2,000",
    "2,500",
    "3,000",
    "3,500",
    "4,000",
    "4,500",
    "5,000",
    "5,500",
    "6,000",
    "6,500",
    "7,000",
    "7,500",
    "8,000",
    "8,500",
    "9,000",
    "9,500",
    "10,000",
    "20,000",
    "30,000",
    "40,000",
    "50,000",
    "60,000",
    "70,000",
    "80,000",
    "90,000",
    "100,000",
    "200,000",
    "300,000",
    "400,000",
    "500,000",
    "600,000",
    "700,000",
    "800,000",
    "900,000",
    "1,000,000",
    "2,000,000",
    "3,000,000",
    "4,000,000",
    "5,000,000",
    "6,000,000",
    "7,000,000",
    "8,000,000",
    "9,000,000"
  ]

  const additionalAmountValues = [
    0,
    500,
    1000,
    1500,
    2000,
    2500,
    3000,
    3500,
    4000,
    4500,
    5000,
    5500,
    6000,
    6500,
    7000,
    7500,
    8000,
    8500,
    9000,
    9500,
    10000,
    20000,
    30000,
    40000,
    50000,
    60000,
    70000,
    80000,
    90000,
    100000,
    200000,
    300000,
    400000,
    500000,
    600000,
    700000,
    800000,
    900000,
    1000000,
    2000000,
    3000000,
    4000000,
    5000000,
    6000000,
    7000000,
    8000000,
    9000000
  ]

  useEffect(() => {
    if (ecReturnContentRef.current.offsetHeight > 72) {
      setShowMoreBtn(true)
      ecReturnContentRef.current.classList.add("line-clamp-3")
    }
  }, [])

  const toggleShowMoreBtn = () => {
    setIsOpen(!isOpen)
    ecReturnContentRef.current.classList.toggle("line-clamp-3")
  }

  const checkedSku = (): EcReturnSku => {
    return ecReturn.ec_return_skus
      .filter((sku) => sku.id === getValues("checked_sku_id"))
      .shift();
  };

  // ===========================
  // オークション関連
  // ===========================
  const isAuction = !!ecReturn.is_auction;
  const auctionMinimumBid = ecReturn.auction_minimum_next_bid ?? ecReturn.price;

  // クイック入札ボタン: EnterProfile / ConfirmSettlement をスキップして即時決済
  const handleQuickBid = () => {
    if (!onQuickBid) return;
    onQuickBid();
  };

  // オークション時のバリデーション (最低入札額以上)
  useEffect(() => {
    if (!isAuction) return;
    const subscription = watch((data) => {
      const value = Number(data.additional_amount);
      setIsTotalAmountValid(!Number.isNaN(value) && value >= auctionMinimumBid);
    });
    return () => subscription.unsubscribe();
  }, [isAuction, auctionMinimumBid, watch]);

  return (
    <>
      { is_target_for_discount && discount_setting?.discount_type !== "introduction" && discountBanner && (
        <div className="mb-3">
          <img
            src={discountBanner}
            className="object-contain"
          />
        </div>
      )}
      <div className="mt-3 mb-[30px] flex">
        <div className="h-[40px] bg-[#1F2122] w-[61%] relative flex items-center justify-center text-white text-sm font-bold">
          <span className="text-[#1F2122] bg-[#768084] rounded-[50%] inline-block mr-1 w-5 h-5 text-center">
            1
          </span>
          支援内容の入力
          <div className="right-triangle-thin right-[-7px] absolute"></div>
        </div>
        <div className="w-[13%] border-[#1F2122] flex items-center justify-center border-b border-r border-t">
          2
        </div>
        <div className="w-[13%] border-[#1F2122] flex items-center justify-center border-b border-r border-t">
          3
        </div>
        <div className="w-[13%] border-[#1F2122] flex items-center justify-center border-b border-r border-t">
          4
        </div>
      </div>
      <div className="mb-5">
        <Slider {...ecReturnSlider}>
          {ecReturn.investment_image_urls.map((imageUrl, index) => (
            <div key={index}>
              <img src={imageUrl} alt={ecReturn.investment_title} />
            </div>
          ))}
        </Slider>
      </div>
      <p className="text-lg mb-5 font-bold">{ecReturn.name}</p>
      <div className="pb-[15px] border-[#D2DFE6] border-b">
        <div ref={ecReturnContentRef}>
          <LinkifyText text={ecReturn.content} />
        </div>
        <button
          className={
            `${showMoreBtn ? "block" : "hidden"}
            ${isOpen
              ? "w-full right-icon icon-arrow-top flex items-center justify-center mt-5 font-raleway after:ml-2 after:mt-1"
              : "w-full icon-arrow-under right-icon flex items-center justify-center mt-5 font-raleway after:ml-2 after:mb-1"}`
          }
          onClick={() => toggleShowMoreBtn()}
          type="button"
        >
          {isOpen ? "Close" : "More"}
        </button>
      </div>
      {isAuction ? (
        <>
          <div className="border-b border-[#D2DFE6] py-10 text-center">
            <div className="font-bold block">
              <span className="text-red left-icon icon-auction"></span>
              オークション型リターン<br />現在の最高入札額
            </div>
            <div className="font-bold text-[40px]">
              {(ecReturn.current_highest_amount ?? ecReturn.price).toLocaleString()}
              <span className="text-lg">円(税込)</span>
            </div>
          </div>
          <div className="border-b border-[#D2DFE6] py-5">
            <div className="font-bold text-center mb-3">全体の入札履歴</div>
            <AuctionBidHistoriesList bidHistories={ecReturn.bid_histories || []} />
          </div>
          <div className="border-[#D2DFE6] py-5 border-b sm:flex sm:justify-between">
            <dl className="flex justify-between items-center sm:after:content-[''] sm:after:rotate-[30deg] sm:after:w-[1px] sm:after:h-full sm:after:bg-[#768084] sm:w-[30%]">
              <dt className="text-[#768084] text-sm icon-human left-icon">入札件数</dt>
              <dd>{ecReturn.support_count.toLocaleString()}件</dd>
            </dl>
            <dl className="flex justify-between items-center sm:after:content-[''] sm:after:rotate-[30deg] sm:after:w-[1px] sm:after:h-full sm:after:bg-[#768084] sm:w-[30%]">
              <dt className="text-[#768084] text-sm icon-human left-icon">入札者数</dt>
              <dd>{(ecReturn.auction_bidder_count ?? 0).toLocaleString()}人</dd>
            </dl>
            <dl className="flex justify-between items-center sm:after:content-[''] sm:w-[30%]">
              <dt className="text-[#768084] text-sm icon-clock-grey left-icon">残り時間</dt>
              <dd className="text-red">
                <AuctionRemainingTime endAt={ecReturn.auction_end_at} realtime />
              </dd>
            </dl>
          </div>
        </>
      ) : (
        <div className="border-[#D2DFE6] py-5 border-b sm:flex sm:justify-between">
          <dl className="flex justify-between items-center sm:after:content-[''] sm:after:rotate-[30deg] sm:after:w-[1px] sm:after:h-full sm:after:bg-[#768084] sm:w-[30%]">
            <dt className="text-[#768084] text-sm icon-human left-icon sm:mr-[10px]">支援数</dt>
            <dd>{ecReturn.support_count.toLocaleString()}件</dd>
          </dl>
          <dl className="flex justify-between items-center sm:after:content-[''] sm:after:rotate-[30deg] sm:after:w-[1px] sm:after:h-full sm:after:bg-[#768084] sm:w-[30%]">
            <dt className="text-[#768084] text-sm icon-stock left-icon sm:mr-[10px]">在庫数</dt>
            <dd>{ecReturn.actual_remaining_number !== null ? `${ecReturn.actual_remaining_number}個` : "余裕あり"}</dd>
          </dl>
          <p className="text-[#FF0000] icon-gift-large left-icon">{ecReturn.is_subscription ? "月額課金中は継続してご提供" : `${ecReturn.schedule_text}に実施予定`}</p>
        </div>
      )}
      {!isAuction && ecReturn.ec_return_skus.length > 0 && (
        <div className="py-[40px] border-[#D2DFE6] border-b">
          <div className="mb-[40px] flex mb-2 items-center">
            <p className="font-bold">{ecReturn.sku_title}</p>
            <span className="bg-[#006AC9] text-xxs justify-center px-1 text-white ml-2">
              必須
            </span>
          </div>
          {ecReturn.ec_return_skus.map((ec_return_sku) => (
            <div
              key={ec_return_sku.id}
              className="first:mt-0 mt-6 sm:flex sm:justify-between sm:mt-4"
            >
              <Radio
                checked={watch("checked_sku_id") === ec_return_sku.id}
                id={`${ec_return_sku.id}`}
                value={ec_return_sku.sku_name}
                label={ec_return_sku.sku_name}
                name={"checked_sku_id"}
                control={control}
                disabled={ec_return_sku.remaining_number <= 0}
                onChange={(e) => {
                  onSkuChange(e);
                  setValue("checked_sku_id", Number(e.target.id) || null);
                }}
                isActive={isSkuActive}
              />
              <div className="flex items-center maxsm:mt-2">
                <span className="text-[#768084] icon-stock left-icon mr-[10px] text-sm">
                  在庫数
                </span>
                {ec_return_sku.remaining_number > 0 ? (
                  <span>{`${ec_return_sku.remaining_number}個`}</span>
                ) : (
                  <span className="text-[#FF0000] font-bold">売り切れ</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {ecReturn.ec_return_questions.slice(0,3).filter(question => question.question_type !== QUESTION_TYPE.NONE).length > 0 && (
        <div className="py-[40px] border-[#D2DFE6] border-b">
          <p className="mb-[40px] font-bold">リターンに関する質問</p>
          {ecReturn.ec_return_questions.slice(0,3).filter(question => question.question_type !== QUESTION_TYPE.NONE).map((question, questionIndex) => (
            <div key={questionIndex} className="mb-[30px]">
              <div className="mb-5">
                <div className="text-[#768084] mb-2 text-xs">質問{questionIndex+1}</div>
                <p>{question.question}</p>
              </div>
              <div className="flex mb-2 items-center">
                <label className="text-[#768084] text-xs" htmlFor="ec_questionnaire1">回答</label>
                <span className="bg-[#006AC9] text-xxs flex items-center justify-center px-1 text-white ml-2">
                  必須
                </span>
              </div>
              {question.question_type === QUESTION_TYPE.TEXT && (
                <TextField
                  id={`question-${questionIndex}`}
                  type="text"
                  placeholder="回答を入力してください"
                  name={`answers.${questionIndex}`}
                  control={control}
                  maxLengthErrorMessage="255文字以内で入力してください"
                  rules={{
                    required: true,
                    maxLength: 255,
                  }}
                  onChange={({ target: { value } }) =>
                    setValue(`answers.${questionIndex}`, value || "")
                  }
                />
              )}
              {question.question_type === QUESTION_TYPE.SELECT && (
                question.ec_return_question_question_answers.map((ans, ansIndex) => (
                  <div key={ansIndex} className="mt-4 first:mt-0">
                    <Radio
                      checked={watch(`answers.${questionIndex}`) === ans.answer}
                      id={`answer-${ansIndex}-of-${questionIndex}`}
                      value={ans.answer}
                      label={ans.answer}
                      name={`answers.${questionIndex}`}
                      control={control}
                      rules={{ required: true }}
                      onChange={(e) => {
                        onQuestionTypeSelectChange(e);
                        const copyAnswers = nowAnswers.slice(0, nowAnswers.length);
                        copyAnswers[questionIndex] = e.target.value; 
                        setNowAnswers(copyAnswers);
                        setValue(`answers.${questionIndex}`, e.target.value || "")
                      }}
                      isActive={isQuestionTypeSelectActive}
                    />
                  </div>
                  )
                )
              )}
              {question.question_type === QUESTION_TYPE.SELECT && errors.answers?.[questionIndex] && (
                <p className="text-[#FF0000] mt-1 text-xs before:content-['*']">必須項目です</p>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="py-[40px]">
        {isAuction ? (
          <>
            <div>
              <div className="flex items-center mb-5">
                <p className="font-bold" id="purchase-amount">入札額(税込)</p>
                <span className="bg-[#006AC9] text-xxs flex items-center justify-center px-1 text-white ml-2">
                  必須
                </span>
              </div>
              <div className="text-[#768084] text-xs mb-2">
                ※現在の最低入札額: {auctionMinimumBid.toLocaleString()}円 (500円刻みで入札)
              </div>
              {/* 入札額を大きく右寄せで表示 (通常リターンの「リターンの購入金額」と同じスタイル) */}
              <div className="text-[34px] mb-5 text-right font-bold">
                {Number(watch("additional_amount") || auctionMinimumBid).toLocaleString()}
                <span className="ml-1 text-lg font-bold">円</span>
              </div>
              {/* 増減コントローラ (口数の代わりに 500円刻みボタン) */}
              <div className="flex items-center justify-end">
                <div className="text-[#768084] mr-3 text-sm">入札額</div>
                <button
                  type="button"
                  className={`${Number(getValues("additional_amount")) > auctionMinimumBid ? "opacity-100" : "opacity-30"} mr-2 w-[40px] h-[40px] rounded-[50%] border-[#1F2122] before:content-[''] before:h-[1px] before:bg-[#1F2122] flex inline-block before:inline-block items-center justify-center before:w-3 border`}
                  onClick={() => {
                    const cur = Number(getValues("additional_amount")) || auctionMinimumBid;
                    setValue("additional_amount", Math.max(auctionMinimumBid, cur - AUCTION_MIN_BID_INCREMENT).toString());
                  }}
                  disabled={Number(getValues("additional_amount")) <= auctionMinimumBid}
                  aria-label="500円減らす"
                />
                <button
                  type="button"
                  className="opacity-100 w-[40px] h-[40px] rounded-[50%] border-[#1F2122] before:content-[''] before:h-[1px] before:bg-[#1F2122] after:content-[''] after:w-[1px] after:bg-[#1F2122] after:absolute flex inline-block before:inline-block items-center justify-center before:w-3 after:h-3 border"
                  onClick={() => {
                    const cur = Number(getValues("additional_amount")) || auctionMinimumBid;
                    setValue("additional_amount", (cur + AUCTION_MIN_BID_INCREMENT).toString());
                  }}
                  aria-label="500円増やす"
                />
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center mb-5">
              <p className="font-bold" id="purchase-amount">リターンの購入金額(税込)</p>
              <span className="bg-[#006AC9] text-xxs flex items-center justify-center px-1 text-white ml-2">
                必須
              </span>
            </div>
            <div className="text-[34px] mb-5 text-right font-bold">
              {priceByAmount().toLocaleString()}
              <span className="ml-1 text-lg font-bold">円</span>
            </div>
            <div className="flex items-center justify-end">
              <div className="text-[#768084] mr-3 text-sm">口数</div>
              <Counter
                control={control}
                setValue={setValue}
                name="number"
                id="number"
                rules={{
                  required: true,
                  pattern: /^[1-9][0-9]{0,1}$|^100$/,
                  max:
                    ecReturn.ec_return_skus?.length > 0
                      ? checkedSku().remaining_number
                      : ecReturn.remaining_number
                }}
                maxNumber={
                  ecReturn.ec_return_skus?.length > 0
                    ? checkedSku().remaining_number
                    : ecReturn.remaining_number
                }
              />
            </div>
            {!ecReturn.is_subscription && (
              <div className="bg-[#F4F6F8] mt-[40px] mb-10 p-5">
                <p className="mb-5 text-sm font-medium">
                  あなたの気持ちをリターン購入金額に上乗せしよう
                </p>
                <div className="mb-[10px] flex items-end justify-between">
                  <SelectBox
                    id="additional_amount"
                    name="additional_amount"
                    placeholder="上乗せ金額を選択してください"
                    control={control}
                    options={additionalAmountOptions}
                    values={additionalAmountValues}
                  />
                  <span className="font-medium ml-1">円</span>
                </div>
                <p className="text-[#768084] text-xs">
                  ※500円以上の支援をお願いします
                </p>
              </div>
            )}
            {is_target_for_discount && discount_setting?.discount_type === "introduction" && (
              <CouponArea
                appliedCoupon={appliedCoupon || { code: "", amount: 0 }}
                onApplyCoupon={handleApplyCoupon}
                onRemoveCoupon={handleRemoveCoupon}
                error={couponError}
              />
            )}
            { appliedCoupon && discountBanner && (
              <div className="mt-3">
                <img
                  src={discountBanner}
                  className="object-contain"
                />
              </div>
            )}
          </>
        )}
      </div>
      <div className="pb-[40px] border-[#D2DFE6] border-b">
        <p className="font-bold">{isAuction ? "お支払い金額(税込)" : "合計の支援金額(税込)"}</p>
        <div className={`${isAuction ? "text-[26px]" : "text-[34px]"} mb-[10px] text-right font-bold`}>
          {totalAmount().toLocaleString()}
          <span className="text-lg">円</span>
        </div>
        {isAuction ? (
          <>
            <p className="text-[#768084] text-sm mb-1">内訳:</p>
            <div className="flex items-end flex-col">
              <div className="text-xs mb-1">
                入札額: {Number(watch("additional_amount") || 0).toLocaleString()}円
              </div>
              <div className="text-xs">
                プラットフォーム利用料: {commission.toLocaleString()}円
              </div>
            </div>
          </>
        ) : ecReturn.is_subscription ?
          (
            <>
              <p className="text-[#768084] text-sm mb-1">内訳:</p>
              <div className="flex items-end flex-col">
                <div className="text-xs mb-1">
                  月額課金リターンの購入金額:{`${priceByAmount().toLocaleString()}円(${getValues("number")}口×${ecReturn.price.toLocaleString()}円)`}
                </div>
                <div className="text-xs mb-1">
                  プラットフォーム利用料:{commission.toLocaleString()}円
                </div>
              </div>
              {!!monthly_destination_value && (
                <>
                  <p className="text-accent text-xs flex items-center justify-end mb-1">
                    現在の支援コース（月額）{monthly_destination_value.toLocaleString()}円
                  </p>
                  <p className="text-accent text-xs flex items-center justify-end">
                    次請求以降の個数の変更ができます。
                  </p>
                </>
              )}
            </>
          )
          :
          (
            <>
              <p className="text-[#768084] text-sm mb-1">内訳:</p>
              <div className="flex items-end flex-col">
                <div className="text-xs mb-1">
                  リターンの購入金額: {priceByAmount().toLocaleString()}円
                </div>
                <div className="text-xs mb-1">
                  上乗せ支援:{Number(watch("additional_amount")).toLocaleString()}円
                </div>
                <div className="text-xs">
                  プラットフォーム利用料:{commission.toLocaleString()}円
                </div>
                {(is_target_for_discount && discount_setting?.discount_type !== "introduction" || appliedCoupon) && (
                  <div className="text-xs text-red-500 mt-1">
                    クーポンの割引金額: -{discountAmount.toLocaleString()}円
                  </div>
                )}
              </div>
            </>
          )
        }
      </div>
      <div className="pt-[40px]">
          {isAuction && ecReturn.has_user_bid_before && !ecReturn.card_expired && (
            <div className="sm:mb-[60px] mb-10">
              <button
                type="button"
                onClick={handleQuickBid}
                disabled={!isTotalAmountValid || is_draft}
                className={`w-full h-[50px] font-bold rounded text-sm ${
                  isTotalAmountValid && !is_draft
                    ? "bg-[#1F2122] text-white hover:opacity-80"
                    : "bg-[#D2DFE6] text-[#768084] cursor-not-allowed"
                }`}
              >
                クイック入札（{Number(watch("additional_amount") || 0).toLocaleString()}円で即時入札）
              </button>
              <p className="text-[#768084] text-xs mt-2 text-center">
                ※お届け先住所は前回入札時のものを引き継ぎ、新たな入札額で即時決済します。お届け先住所を変更したい場合は <a href="/mypage/support_history" className="underline link-hover-none">支援履歴</a> より変更可能です。
              </p>
              {!isTotalAmountValid && (
                <p className="text-[#FF0000] text-xs mt-1 text-center">
                  上部の+/-ボタンで入札額を最低入札額（{auctionMinimumBid.toLocaleString()}円）以上に調整してください。
                </p>
              )}
            </div>
          )}
          {isAuction && ecReturn.has_user_bid_before && ecReturn.card_expired && (
            <div className="sm:mb-[60px] mb-10 p-4 bg-[#F4F6F8] text-[#FF0000] text-sm">
              ※クレジットカードの有効期限が切れているため、クイック入札はご利用いただけません。マイページからカード情報を更新してください。
            </div>
          )}
          <div className="sm:mb-[60px] mb-10">
            <p className="font-bold mb-6">プラットフォーム利用料とは</p>
            <p className="mb-[10px]">プラットフォーム利用料は、その一部を『スポチュニティ基金』として社会貢献活動を行う団体へ寄付すると共に、安心安全な運営及びシステム環境の提供のために利用します。<br /><br />寄付の実施可否および時期は、スポチュニティにより決定され、その実施を保証するものではありません。</p>
            <p>詳しくは
              <a href="https://corp.spportunity.com/platform-fee/" target="_blank" rel="noopener noreferrer" className="underline link-hover-none">
                こちら
              </a>
            </p>
          </div>
        {!ecReturn.is_subscription && (
          <div className="sm:mb-[60px] mb-10">
            <p className="font-bold mb-6">{isAuction ? "オークション型リターンについて" : "リターンについて"}</p>
            {isAuction ? (
              <>
                <p>オークション型リターンは、オークション期限終了まで、最高額の入札者が落札して商品を受け取れる仕組みです。</p>
                <p>入札はクレジットカードで決済が行われます。入札完了後のキャンセルはできません。</p>
                <p>最高額入札者を他の入札者が上回らない限り落札権は最高入札者にあります。一方、他の入札者が高値で入札した際は、自動的に返金処理が行われ、請求は発生しません。</p>
                <p>スポチュニティはプロジェクトオーナーのプロジェクト実行能力を保証するものではありません。プロジェクトの実行やリターンの発送（履行）などは、全てプロジェクトオーナーの責任のもと行われます。</p>
              </>
            ) : (
              <>
                <p>リターンはご支援いただいた方、全員へお渡しいたします。</p>
                <p>リターンが配布されるのは必ずしも購入直後ではありませんのでご注意下さい。</p>
                <p>スポチュニティはプロジェクトオーナーのプロジェクト実行能力を保証するものではありません。プロジェクトの実行やリターンの発送（履行）などは、全てプロジェクトオーナーの責任のもと行われます。</p>
              </>
            )}
          </div>
        )}
        <div className="mb-10 p-5 bg-[#F4F6F8]">
          <p className="font-bold mb-2">リターンに関するお問い合わせ先</p>
          <a href={`mailto:${projectInformation.owner_email}`} className="link-hover-none underline">{projectInformation.owner_email}</a>
        </div>
        <p className="text-[#768084] mb-[40px] text-xs">
          リターンの購入にあたり、
          <a
            href="https://corp.spportunity.com/term_of_use/"
            className="link-hover-none underline"
          >
            スポチュニティのサービス利用規約
          </a>
          および
          <a
            href="https://corp.spportunity.com/privacy_policy/"
            className="link-hover-none underline"
          >
            プライバシーポリシー
          </a>
          に同意したものとみなされます 。
        </p>
        {ecReturn.is_subscription && (
          <div className="text-[#FF0000] bg-[#F4F6F8] p-5 sm:mb-[60px] mb-10">
            <p>※選択された支援コースの金額は月額で自動課金されます。</p>
            <p>※選択された支援コースは、後から変更・キャンセル(解約)できます。</p>
          </div>
        )}
        {/*
          オークションのクイック入札ボタン表示時 (前回入札済み + カード有効) は、
          画面内の導線が二重になるため、下部の「お支払い情報の入力へ進む」ボタンを非表示にする
        */}
        {!(isAuction && ecReturn.has_user_bid_before && !ecReturn.card_expired) && (
          <div className="mb-[60px] sm:mx-auto h-[50px] sm:w-[290px]">
            <BaseButton
              text="お支払い情報の入力へ進む"
              fontSize="text-sm"
              isSubmit={true}
              disabled={!isTotalAmountValid || is_draft}
            />
            { !isTotalAmountValid && (
              <p className="text-xs text-red-500 mt-1 text-center">
                {isAuction
                  ? `入札額が現在の最低入札額(${auctionMinimumBid.toLocaleString()}円)に満たないため、このままお手続きを進めることができません。`
                  : "合計支援金額が500円に満たないため、このままお手続きを進めることができません。"}
              </p>
            )}
          </div>
        )}
        {ecReturn.is_subscription && !!monthly_destination_value && (
          <>
            <ConfirmationModal
              isActive={isModalOpen}
              toggleModal={toggleModal}
              confirmText="OK"
              onSubmit={onCancel}
            >
              <div className="sm:text-left">
                <p>現在の支援コースをキャンセル(解約)しますか？</p>
              </div>        
            </ConfirmationModal>
            <div className="mt-10 sm:w-[315px] h-[60px] sm:mx-auto mb-10">
              <SecondaryButton
                text={`現在の支援コースをキャンセル(解約)する`}
                onClick={() => setIsModalOpen(true)}
              />
            </div>
          </>
        )}
        <div className="text-center">
          <a
            href={is_draft ? `${projectInformation.ec_return_path}${window.location.search}` : projectInformation.ec_return_path}
            className="left-icon icon-stick-arrow-left text-sm font-medium"
          >
            リターン詳細ページに戻る
          </a>
        </div>
        <div className="text-center mt-10">
          <a
            href={is_draft ? `${projectInformation.investment_path}${window.location.search}` : projectInformation.investment_path}
            className="left-icon icon-stick-arrow-left text-sm font-medium"
          >
            プロジェクトの詳細ページに戻る
          </a>
        </div>
      </div>
    </>
  );
};

export default SelectReturn;
